import React, { useEffect, useState } from 'react'
import { Card, Select, Table, Tag, Progress, Typography, Row, Col } from 'antd'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts'
import { GET_SLA_MONITORING } from '../graphql/Queries'
import { useQuery } from '@apollo/react-hooks'
import './SlaMonitoringDashboard.css'
import moment from 'moment'
import { DatePicker } from 'antd'
import TicketLoadTable from './TicketLoadTable'

const { Option } = Select
const { Text } = Typography

export default function SlaMonitoringDashboard() {
  const { RangePicker } = DatePicker

  const [timeRange, setTimeRange] = useState('THIS_WEEK')
  const [analysisData, setAnalysisData] = useState(null)

  const [currentPage, setCurrentPage] = useState(1)
  // const [slaConfigData, setSlaConfigData] = useState([])
  const [currentTime, setCurrentTime] = useState(moment())
  const [customFrom, setCustomFrom] = useState(null)
  const [customTo, setCustomTo] = useState(null)

  useEffect(() => {
    if (timeRange === 'THIS_WEEK') {
      setCustomFrom(moment().startOf('week'))
      setCustomTo(moment().endOf('week'))
    } else if (timeRange === 'THIS_MONTH') {
      setCustomFrom(moment().startOf('month'))
      setCustomTo(moment().endOf('month'))
    } else if (timeRange === 'THIS_YEAR') {
      setCustomFrom(moment().startOf('year'))
      setCustomTo(moment().endOf('year'))
    }
  }, [timeRange])

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(moment())
    }, 1000)

    return () => clearInterval(timer)
  }, [])
  const pageSize = 10

  const { loading, refetch } = useQuery(GET_SLA_MONITORING, {
    variables: {
      timeRange,
      customFrom: customFrom ? customFrom.format('YYYY-MM-DD') : null,
      customTo: customTo ? customTo.format('YYYY-MM-DD') : null,
      page: currentPage,
      limit: pageSize,
    },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setAnalysisData(data.getSlaMonitoring)
    },
  })

  // useEffect(() => {
  //   fetchSLAConfigStatus()
  // }, [])
  const priorityColor = {
    high: 'red',
    medium: 'orange',
    low: 'green',
  }

  // const fetchSLAConfigStatus = async () => {
  //   try {
  //     const res = await client.query({
  //       query: GET_PRIORITY_SLA_CONFIG,
  //       fetchPolicy: 'network-only',
  //     })

  //     const data = res?.data?.getPrioritySlaConfigs || []
  //     setSlaConfigData(data)
  //   } catch (err) {
  //     console.error('Error fetching SLA config:', err)
  //   }
  // }

  const calculateRealTimeSLATime = (ticket) => {
    if (!ticket?.duration) {
      return { label: '—', isNegative: false }
    }

    const ticketDurationAt = moment(ticket.duration)
    const slaDeadline = ticketDurationAt.clone()
    const now = currentTime.clone()

    // Calculate remaining time
    const remainingTime = moment.duration(slaDeadline.diff(now))
    const isNegative = remainingTime.asMilliseconds() < 0
    const absoluteTime = moment.duration(
      Math.abs(remainingTime.asMilliseconds())
    )

    const days = absoluteTime.days()
    const hours = String(absoluteTime.hours()).padStart(2, '0')
    const minutes = String(absoluteTime.minutes()).padStart(2, '0')
    const seconds = String(absoluteTime.seconds()).padStart(2, '0')

    let formatted
    if (days > 0) {
      formatted = `${days}d ${hours}:${minutes}:${seconds}`
    } else {
      formatted = `${hours}:${minutes}:${seconds}`
    }

    return {
      label: isNegative ? `-${formatted}` : formatted,
      isNegative,
      rawValue: remainingTime.asMilliseconds(), // For sorting
    }
  }

  // Get SLA color based on currentLevel
  const getSLAColor = (ticket) => {
    const currentLevel = ticket?.currentLevel
    console.log('currentLevel', currentLevel)

    if (!currentLevel) {
      return { background: '#7E82991A', color: '#7E8299' } // Default color
    }

    switch (currentLevel) {
      case 'TEAM_LEADER':
        return { background: '#7E82991A', color: '#7E8299' } // Normal color
      case 'DEPARTMENT_HEAD':
        return { background: '#FB7B041A', color: '#FB7B04' } // Orange color
      case 'CRM_ADMIN':
        return { background: '#AF00001A', color: '#AF0000' } // Red color
      default:
        return { background: '#7E82991A', color: '#7E8299' }
    }
  }

  useEffect(() => {
    setCurrentPage(1)
  }, [timeRange])

  useEffect(() => {
    refetch({
      timeRange,
      customFrom: customFrom ? customFrom.format('YYYY-MM-DD') : null,
      customTo: customTo ? customTo.format('YYYY-MM-DD') : null,
      page: currentPage,
      limit: pageSize,
    })
  }, [timeRange, currentPage, customFrom, customTo, refetch, pageSize])

  const trendChart = analysisData?.breachTrend?.trend || []
  const delta = analysisData?.breachTrend?.deltaPercent || 0

  const columns = [
    {
      title: 'Ticket ID',
      dataIndex: 'ticketNumber',
      render: (text) => <span className='ticket-id'>#{text}</span>,
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      render: (date) => <span className='created-date'>{date}</span>,
    },
    {
      title: 'Department',
      dataIndex: 'department',
      render: (dept) => <span className='department-name'>{dept}</span>,
    },
    {
      title: 'SLA Time',
      dataIndex: 'slaTime',
      key: 'slaTime',
      align: 'left',
      width: 130,

      render: (text, record) => {
        const { label, isNegative } = calculateRealTimeSLATime(record)
        const slaColor = getSLAColor(record)

        return record?.status === 'RESOLVED' ? (
          <span style={{ fontSize: 10 }}>
            Resolved At
            <p style={{ fontSize: 12, fontWeight: 600 }}>
              {moment(record?.resolvedAt).format('DD-MM-YYYY hh:mm A')}
            </p>
          </span>
        ) : (
          <span
            style={{
              background: slaColor.background,
              color: slaColor.color,
              padding: '2px 6px',
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 700,
              animation: isNegative ? 'blink 1s infinite' : 'none',
            }}
          >
            {/* {isNegative && '⚠️ '} */}
            {label}
          </span>
        )
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status) => {
        const color =
          status === 'BREACHED'
            ? '#FF4D4F'
            : status === 'ESCALATED'
            ? '#AF0000'
            : status === 'RESOLVED'
            ? '#52C41A'
            : '#1890FF'
        return (
          <Tag color={color} className='status-tag'>
            {status}
          </Tag>
        )
      },
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 120,
      align: 'left',

      render: (priority) => {
        const icon =
          priority === 'high' || priority === 'medium'
            ? '⚠️'
            : priority === 'low'
            ? '✓'
            : ''

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Tag
              color={priorityColor[priority?.toLowerCase()]}
              style={{
                borderRadius: '4px',
                border: 'none',
                fontSize: '12px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                cursor: 'pointer',
              }}
            >
              {icon} {priority}
            </Tag>
          </div>
        )
      },
    },
    {
      title: 'Agent',
      dataIndex: 'agent',
      render: (agent) => <span className='agent-name'>{agent}</span>,
    },
  ]

  return (
    <div className='sla-monitoring-container'>
      {/* <div className='sla-header'>
        <Title level={3} className='dashboard-title'>
          SLA Monitoring Dashboard
        </Title>
      </div> */}

      <Row gutter={[16, 16]} className='sla-content'>
        {/* Left Column - Main Charts and Table */}
        <Col xs={24} lg={14}>
          <div className='left-column'>
            {/* Breach Trend Chart */}
            <Card
              style={{ marginBottom: '0px' }}
              className='breach-trend-card'
              bordered={false}
            >
              <div className='chart-header-section'>
                <div className='trend-info'>
                  <div className='trend-stats'>
                    <span className='breach-count'>
                      {analysisData?.breachTrend?.totalBreached || 0} Tickets
                    </span>
                    <Tag
                      color={delta >= 0 ? '#AF0000' : '#52C41A'}
                      className='delta-tag'
                    >
                      {delta >= 0 ? `↑ ${delta}%` : `↓ ${Math.abs(delta)}%`}
                    </Tag>
                  </div>
                  <Text type='secondary' className='chart-subtitle'>
                    Breached Tickets This Period
                  </Text>
                </div>

                {timeRange === 'CUSTOM' && (
                  <RangePicker
                    value={[customFrom, customTo]}
                    format='YYYY-MM-DD'
                    onChange={(dates) => {
                      setCustomFrom(dates?.[0] || moment().startOf('week'))
                      setCustomTo(dates?.[1] || moment().endOf('week'))
                    }}
                  />
                )}
                <Select
                  size='small'
                  value={timeRange}
                  onChange={setTimeRange}
                  className='time-range-selector'
                >
                  <Option value='THIS_WEEK'>This Week</Option>
                  <Option value='THIS_MONTH'>This Month</Option>
                  <Option value='THIS_YEAR'>This Year</Option>
                  <Option value='CUSTOM'>Custom Range</Option>
                </Select>
              </div>

              <div className='chart-container'>
                <ResponsiveContainer width='100%' height={220}>
                  <LineChart
                    data={trendChart}
                    margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray='3 3' stroke='#f0f0f0' />
                    <XAxis
                      dataKey='label'
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#666' }}
                    />
                    <YAxis
                      dataKey='breached'
                      allowDecimals={false}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#666' }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                    />
                    <Line
                      type='monotone'
                      dataKey='breached'
                      stroke='#1890FF'
                      strokeWidth={3}
                      dot={{ fill: '#1890FF', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#1890FF', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* Tickets Table */}
            <Card
              style={{ marginBottom: '0px' }}
              className='tickets-table-card'
              title={<span className='table-title'>Recent SLA Tickets</span>}
              bordered={false}
            >
              <div className='table-wrapper'>
                <Table
                  columns={columns}
                  dataSource={analysisData?.ticketTable?.rows || []}
                  pagination={{
                    current: currentPage,
                    pageSize,
                    total: analysisData?.ticketTable?.total || 0,
                    showSizeChanger: false,
                    onChange: (page) => setCurrentPage(page),
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} tickets`,
                  }}
                  rowKey='id'
                  size='small'
                  className='sla-tickets-table'
                  loading={loading}
                />
              </div>
            </Card>
          </div>
        </Col>

        {/* Right Column - Side Widgets */}
        <Col xs={24} lg={10}>
          <div className='right-column'>
            {/* Upcoming SLA Breaches */}
            <Card
              style={{ marginBottom: '0px' }}
              title={
                <span className='widget-title'>Upcoming SLA Breaches</span>
              }
              className='upcoming-breaches-card'
              bordered={false}
            >
              <div className='upcoming-breaches-table-wrapper'>
                <Table
                  columns={[
                    {
                      title: 'ID',
                      dataIndex: 'ticketNumber',
                      key: 'ticketNumber',
                      width: 80,
                      render: (text) => (
                        <span className='ticket-id'>#{text}</span>
                      ),
                    },
                    {
                      title: 'Dept',
                      dataIndex: 'department',
                      key: 'department',
                      width: 100,
                      render: (dept) => (
                        <span className='department-name'>{dept}</span>
                      ),
                    },
                    {
                      title: 'SLA Time',
                      dataIndex: 'slaTime',
                      key: 'slaTime',
                      width: 100,
                      render: (time) => (
                        <span className='sla-time-remaining'>{time}</span>
                      ),
                    },
                  ]}
                  dataSource={analysisData?.upcomingBreaches || []}
                  pagination={{
                    pageSize: 5,
                    size: 'small',
                    showSizeChanger: false,
                    showQuickJumper: false,
                    showTotal: (total, range) => `${range[0]} of ${total}`,
                  }}
                  rowKey='id'
                  size='small'
                  className='upcoming-breaches-table'
                  locale={{
                    emptyText: (
                      <div className='no-breaches'>
                        <Text type='secondary' className='no-data-text'>
                          No upcoming breaches
                        </Text>
                      </div>
                    ),
                  }}
                />
              </div>
            </Card>

            {/* SLA Compliance Rate */}
            <Card
              style={{ marginBottom: '0px' }}
              title={<span className='widget-title'>SLA Compliance Rate</span>}
              className='compliance-rate-card'
              bordered={false}
            >
              <div className='compliance-content'>
                <div className='progress-wrapper'>
                  <Progress
                    type='dashboard'
                    percent={analysisData?.complianceRate?.percentage || 0}
                    strokeColor='#3E97FF' // use single color first, then try gradient
                    strokeWidth={8}
                    size={200}
                    format={(percent) => (
                      <div className='progress-text'>
                        <div className='percentage'>{percent}%</div>
                        <div className='compliance-label'>Compliance</div>
                      </div>
                    )}
                  />
                </div>
              </div>
            </Card>

            {/* Escalation Chart */}
            <Card
              style={{ marginBottom: '0px' }}
              title={
                <span className='widget-title'>Department Escalations</span>
              }
              className='escalation-chart-card'
              bordered={false}
            >
              <div>
                <ResponsiveContainer
                  width='100%'
                  height={analysisData?.escalationChart?.length * 40}
                >
                  <BarChart
                    data={analysisData?.escalationChart || []}
                    layout='vertical'
                    margin={{ top: 10, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray='3 3' stroke='#f0f0f0' />
                    <XAxis
                      type='number'
                      allowDecimals={false}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#666' }}
                    />
                    <YAxis
                      type='category'
                      dataKey='department'
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#666' }}
                      width={120} // give enough room for long labels
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                    />
                    <Bar
                      dataKey='count'
                      fill='#1890FF'
                      radius={[0, 4, 4, 0]}
                      maxBarSize={20}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        </Col>
      </Row>
      {/* Ticket Load section */}
      <Row justify='center'>
        <Col xs={24} style={{ display: 'flex', justifyContent: 'center' }}>
          <Card
            style={{ marginBottom: '0px', width: '100%', maxWidth: 1200 }}
            title={<span className='widget-title'>Ticket Load</span>}
            className='escalation-chart-card'
            bordered={false}
          >
            <TicketLoadTable />
          </Card>
        </Col>
      </Row>
    </div>
  )
}
